import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useCaseApi } from "../api/case/parse/ParseCaseClient";
import { canWrite } from "../shared/AccessList";
import { CaseParameters } from "../shared/CaseModel";
import { CASE_EXPORT_VERSION, isCaseExportFile } from "../shared/caseExport";
import {
  CaseDecryptionError,
  CaseVersionError,
  decryptConfig,
} from "./caseCrypto";

type Step = "file" | "password" | "confirmExists";

export const ImportCaseDialog = ({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) => {
  const { t } = useTranslation(undefined, {
    keyPrefix: "case.list.importDialog",
  });
  const caseApi = useCaseApi();
  const navigate = useNavigate();
  const theme = useTheme();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [step, setStep] = useState<Step>("file");
  const [fileName, setFileName] = useState<string>();
  const [envelope, setEnvelope] = useState<unknown>();
  const [password, setPassword] = useState("");
  const [config, setConfig] = useState<CaseParameters>();
  // the case the file would overwrite is one the user may only read
  const [existingReadOnly, setExistingReadOnly] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();

  const close = () => {
    setStep("file");
    setFileName(undefined);
    setEnvelope(undefined);
    setPassword("");
    setConfig(undefined);
    setExistingReadOnly(false);
    setBusy(false);
    setError(undefined);
    onClose();
  };

  const onFileChosen = async (file: File) => {
    setError(undefined);
    setFileName(file.name);
    try {
      const parsed = JSON.parse(await file.text());
      if (!isCaseExportFile(parsed)) {
        setError(t("invalidFile"));
        return;
      }
      if (
        typeof parsed.version !== "number" ||
        parsed.version > CASE_EXPORT_VERSION
      ) {
        setError(t("unsupportedVersion"));
        return;
      }
      setEnvelope(parsed);
      setPassword("");
      setStep("password");
    } catch {
      setError(t("invalidFile"));
    }
  };

  const decrypt = async () => {
    if (password.trim().length === 0) return;
    setBusy(true);
    setError(undefined);
    try {
      const decrypted = await decryptConfig(envelope, password);
      setConfig(decrypted);
      // does a case with this id already exist (and is accessible)?
      let exists = false;
      if (decrypted.id) {
        try {
          const [existing, accessNames] = await Promise.all([
            caseApi.queryCase(decrypted.id),
            caseApi.queryAccessNames(),
          ]);
          exists = true;
          setExistingReadOnly(!canWrite(existing.accessList, accessNames));
        } catch {
          // not found or no access -> import as a new case
          exists = false;
        }
      }
      if (exists) {
        setBusy(false);
        setStep("confirmExists");
      } else {
        await runImport(decrypted, {});
      }
    } catch (e) {
      setBusy(false);
      if (e instanceof CaseVersionError) {
        setError(t("unsupportedVersion"));
      } else {
        setError(
          e instanceof CaseDecryptionError ? t("wrongPassword") : t("error"),
        );
      }
    }
  };

  const runImport = async (
    cfg: CaseParameters,
    options: { overwriteId?: string },
  ) => {
    setBusy(true);
    setError(undefined);
    try {
      const imported = await caseApi.importCase(cfg, options);
      close();
      navigate(`/cases/${imported.id}`);
    } catch (e) {
      console.warn("Failed to import case", e);
      setBusy(false);
      setError(t("error"));
    }
  };

  const importAsCopy = () =>
    config &&
    runImport({ ...config, name: t("copyName", { name: config.name }) }, {});

  const importOverwrite = () =>
    config && runImport(config, { overwriteId: config.id });

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : close}
      fullWidth
      maxWidth="xs"
    >
      <DialogTitle>{t("title")}</DialogTitle>
      <DialogContent
        sx={{ display: "flex", flexDirection: "column", gap: theme.spacing(1) }}
      >
        {step === "file" && (
          <>
            <DialogContentText>{t("selectFile")}</DialogContentText>
            <input
              ref={fileInputRef}
              type="file"
              accept=".casesolv,application/json"
              style={{ display: "none" }}
              onChange={(e) => {
                const file = e.target.files?.[0];
                // reset so re-selecting the same file fires onChange again
                e.target.value = "";
                if (file) void onFileChosen(file);
              }}
            />
            <Stack
              direction="row"
              spacing={2}
              sx={{
                alignItems: "center",
              }}
            >
              <Button
                variant="outlined"
                onClick={() => fileInputRef.current?.click()}
              >
                {t("chooseFile")}
              </Button>
              {fileName && (
                <Typography variant="body2" color="textSecondary" noWrap>
                  {fileName}
                </Typography>
              )}
            </Stack>
          </>
        )}

        {step === "password" && (
          <>
            <DialogContentText>
              {t("passwordIntro", { name: fileName })}
            </DialogContentText>
            <TextField
              autoFocus
              required
              margin="dense"
              label={t("password")}
              type="password"
              fullWidth
              variant="outlined"
              value={password}
              disabled={busy}
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") void decrypt();
              }}
            />
          </>
        )}

        {step === "confirmExists" && (
          <DialogContentText>
            {t(existingReadOnly ? "existsReadOnlyQuestion" : "existsQuestion")}
          </DialogContentText>
        )}

        {error && (
          <Alert sx={{ mt: 2 }} severity="error">
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={close} disabled={busy}>
          {t("cancel")}
        </Button>
        {step === "password" && (
          <Button
            variant="contained"
            disabled={password.trim().length === 0 || busy}
            loading={busy}
            onClick={decrypt}
          >
            {t("decrypt")}
          </Button>
        )}
        {step === "confirmExists" && (
          <>
            <Button
              color="warning"
              variant="outlined"
              disabled={busy || existingReadOnly}
              loading={busy}
              onClick={importOverwrite}
            >
              {t("overwrite")}
            </Button>
            <Button
              variant="contained"
              disabled={busy}
              loading={busy}
              onClick={importAsCopy}
            >
              {t("copy")}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};
